import { useEffect, useRef, useState } from 'react'

// Le WakeLock empêche l'écran de s'éteindre pendant l'utilisation : c'est un
// confort, jamais un prérequis. Tout échec doit donc rester silencieux.
//
// - Android/Chrome : la demande est accordée sans interaction.
// - iOS/Safari (>= 16.4) : l'API existe (le garde-fou `navigator.wakeLock` ne
//   suffit pas) mais la demande est refusée par un NotAllowedError en mode
//   économie d'énergie ou quand la page n'est pas au premier plan. iOS relâche
//   aussi le verrou dès que la page passe en arrière-plan.
//
// On retente donc au retour au premier plan et à la première interaction de
// l'utilisateur, ce qui couvre le cas iOS sans rien casser sur Android.
const useWakeLock = () => {
  const [sentinel, setSentinel] = useState(null)
  const sentinelRef = useRef(null)

  useEffect(() => {
    if (!navigator.wakeLock) {
      console.log('WakeLock API non disponible')
      return
    }

    let cancelled = false
    let pending = false

    const request = async () => {
      if (cancelled || pending || sentinelRef.current) return
      if (document.visibilityState !== 'visible') return

      pending = true
      try {
        const lock = await navigator.wakeLock.request('screen')
        if (cancelled) {
          lock.release().catch(() => {})
          return
        }
        lock.addEventListener('release', () => {
          sentinelRef.current = null
          setSentinel(null)
        })
        sentinelRef.current = lock
        setSentinel(lock)
        console.log('WakeLock actif')
      } catch (err) {
        // NotAllowedError sur iOS : économie d'énergie, page en arrière-plan…
        console.log('WakeLock refusé : %s', err && err.message ? err.message : err)
      } finally {
        pending = false
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') request()
    }

    request()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pointerdown', request)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pointerdown', request)
      const lock = sentinelRef.current
      sentinelRef.current = null
      if (lock) lock.release().catch(() => {})
    }
  }, [])

  return sentinel
}

export default useWakeLock
